import React from "react";
import { projectsApi } from "../api/projects";

const ProjectsContext = React.createContext(null);
const ACTIVE_PROJECT_KEY = "webprofit_active_project_slug";

function projectMatches(project, key) {
  if (!project || !key) return false;
  return [project.slug, project.id, project._id]
    .filter(Boolean)
    .some((value) => String(value) === String(key));
}

function mergeProject(currentProject, nextProject) {
  if (!nextProject) return currentProject;
  if (!currentProject) return nextProject;

  return {
    ...currentProject,
    ...nextProject,
    billing: {
      ...(currentProject.billing || {}),
      ...(nextProject.billing || {}),
    },
    deployment: {
      ...(currentProject.deployment || {}),
      ...(nextProject.deployment || {}),
    },
    modules: {
      ...(currentProject.modules || {}),
      ...(nextProject.modules || {}),
    },
    capabilities: {
      ...(currentProject.capabilities || {}),
      ...(nextProject.capabilities || {}),
    },
  };
}

export function ProjectsProvider({ children }) {
  const [projects, setProjects] = React.useState([]);
  const [activeProject, setActiveProjectState] = React.useState(null);
  const [projectCreation, setProjectCreation] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const projectsRef = React.useRef([]);
  const loadProjectsPromiseRef = React.useRef(null);

  React.useEffect(() => {
    projectsRef.current = projects;
  }, [projects]);

  const applyProjectUpdate = React.useCallback((nextProject) => {
    if (!nextProject) return null;

    setProjects((current) => {
      const index = current.findIndex((project) =>
        projectMatches(project, nextProject.slug || nextProject.id || nextProject._id)
      );

      if (index < 0) {
        const next = [nextProject, ...current];
        projectsRef.current = next;
        return next;
      }

      const next = [...current];
      next[index] = mergeProject(current[index], nextProject);
      projectsRef.current = next;
      return next;
    });

    setActiveProjectState((current) => {
      if (!current || !projectMatches(current, nextProject.slug || nextProject.id || nextProject._id)) {
        return current;
      }
      return mergeProject(current, nextProject);
    });

    return nextProject;
  }, []);

  const loadProjects = React.useCallback(async () => {
    if (loadProjectsPromiseRef.current) {
      return loadProjectsPromiseRef.current;
    }

    const request = (async () => {
      try {
        setLoading(true);
        setError("");
        const data = await projectsApi.list();
        const list = data.projects || [];
        projectsRef.current = list;
        setProjects(list);
        setProjectCreation(data.projectCreation || null);

        const storedSlug = localStorage.getItem(ACTIVE_PROJECT_KEY);
        const storedProject = list.find((project) => project.slug === storedSlug);
        const nextActive = storedProject || list[0] || null;
        setActiveProjectState((current) => {
          if (!nextActive) return null;
          if (current === nextActive) return current;
          return current && projectMatches(current, nextActive.slug || nextActive.id || nextActive._id)
            ? mergeProject(current, nextActive)
            : nextActive;
        });
        if (nextActive?.slug) localStorage.setItem(ACTIVE_PROJECT_KEY, nextActive.slug);

        return list;
      } catch (err) {
        projectsRef.current = [];
        setProjects([]);
        setActiveProjectState(null);
        setProjectCreation(null);
        setError(err.message || "No se pudieron cargar los proyectos");
        return [];
      } finally {
        setLoading(false);
      }
    })();

    loadProjectsPromiseRef.current = request;
    try {
      return await request;
    } finally {
      if (loadProjectsPromiseRef.current === request) {
        loadProjectsPromiseRef.current = null;
      }
    }
  }, []);

  const setActiveProject = React.useCallback((projectOrSlug) => {
    setActiveProjectState((current) => {
      const project =
        typeof projectOrSlug === "string"
          ? projectsRef.current.find(
              (item) => item.slug === projectOrSlug || item.id === projectOrSlug || item._id === projectOrSlug,
            )
          : projectOrSlug;

      if (!project) return current;
      if (project?.slug) localStorage.setItem(ACTIVE_PROJECT_KEY, project.slug);
      if (current === project) return current;
      return current && projectMatches(current, project.slug || project.id || project._id)
        ? mergeProject(current, project)
        : project;
    });
  }, []);

  React.useEffect(() => {
    const handleBillingRestriction = (event) => {
      const detail = event?.detail || {};
      const path = String(detail.path || "");
      const match = path.match(/\/projects\/([^/?#]+)/);
      const projectKey = match ? decodeURIComponent(match[1]) : "";
      const access = detail.access || {};

      const applyRestriction = (project) => {
        if (!project) return project;
        const matches =
          !projectKey ||
          project.slug === projectKey ||
          project.id === projectKey ||
          project._id === projectKey;
        if (!matches) return project;

        return {
          ...project,
          billing: {
            ...(project.billing || {}),
            status: project.billing?.status || "past_due",
            accessMode: access.mode || "landing_only",
            graceUntil: access.graceUntil || project.billing?.graceUntil || null,
            restrictedAt: access.restrictedAt || project.billing?.restrictedAt || new Date().toISOString(),
          },
        };
      };

      setProjects((current) => {
        const next = current.map(applyRestriction);
        projectsRef.current = next;
        return next;
      });
      setActiveProjectState((current) => applyRestriction(current));
    };

    window.addEventListener("webprofit:billing-restricted", handleBillingRestriction);
    return () => window.removeEventListener("webprofit:billing-restricted", handleBillingRestriction);
  }, []);

  const createProject = async (payload) => {
    setError("");
    try {
      const data = await projectsApi.create(payload);
      if (data.projectCreation) setProjectCreation(data.projectCreation);

      const createdProject = data.project
        ? {
            ...data.project,
            deployment: data.deployment || data.project.deployment,
          }
        : null;

      if (createdProject) {
        applyProjectUpdate(createdProject);
        setActiveProjectState(createdProject);
        if (createdProject.slug) {
          localStorage.setItem(ACTIVE_PROJECT_KEY, createdProject.slug);
        }
      }

      await loadProjects();
      if (createdProject) setActiveProject(createdProject);
      return createdProject;
    } catch (error) {
      if (error?.data?.projectCreation) {
        setProjectCreation(error.data.projectCreation);
      }
      throw error;
    }
  };

  const refreshProjectDeployment = React.useCallback(async (projectId) => {
    const data = await projectsApi.getDeployment(projectId);
    const nextProject = data.project
      ? {
          ...data.project,
          deployment: data.deployment || data.project.deployment,
        }
      : null;

    if (nextProject) applyProjectUpdate(nextProject);
    return nextProject;
  }, [applyProjectUpdate]);

  const retryProjectDeployment = React.useCallback(async (projectId) => {
    const data = await projectsApi.retryDeployment(projectId);
    const nextProject = data.project
      ? {
          ...data.project,
          deployment: data.deployment || data.project.deployment,
        }
      : null;

    if (nextProject) applyProjectUpdate(nextProject);
    return nextProject;
  }, [applyProjectUpdate]);

  const value = React.useMemo(
    () => ({
      projects,
      activeProject,
      projectCreation,
      loading,
      error,
      loadProjects,
      createProject,
      setActiveProject,
      refreshProjectDeployment,
      retryProjectDeployment,
    }),
    [
      projects,
      activeProject,
      projectCreation,
      loading,
      error,
      loadProjects,
      setActiveProject,
      refreshProjectDeployment,
      retryProjectDeployment,
    ]
  );

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
}

export function useProjects() {
  const context = React.useContext(ProjectsContext);
  if (!context) throw new Error("useProjects debe usarse dentro de ProjectsProvider");
  return context;
}
